import Vue from "vue";

import GuaranteePrintBlock from "@/components/ReportsAndPrintings/GuaranteePrintBlock.vue";

const DEFAULT_PAGE_SELECTOR = ".subpage";
const DEFAULT_FIT_MARGIN_PX = 18;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function hasEmptyGuaranteeCache(vm, { businessId, branchId, guaranteeType }) {
  const api = vm && vm.$API && vm.$API.businessGuarantees;
  if (!api || typeof api.peekEffectiveCache !== "function") {
    return false;
  }

  const entry = api.peekEffectiveCache({
    ID_Business: businessId,
    ID_Branch: branchId,
    GuaranteeType: guaranteeType,
  });

  return entry?.status === "empty";
}

export function createGuaranteePlacementState() {
  return {
    guaranteeHtml: "",
    showGuaranteeInline: false,
    guaranteePlacementResolved: false,
    guaranteePlacementToken: 0,
  };
}

export async function loadGuaranteeHtml(vm, {
  businessId,
  branchId,
  guaranteeType,
}) {
  if (!businessId || !vm || !vm.$API || !vm.$API.businessGuarantees) {
    return "";
  }

  try {
    const response = await vm.$API.businessGuarantees.getEffective({
      ID_Business: businessId,
      ID_Branch: branchId,
      GuaranteeType: guaranteeType,
    });

    return (response && response.data && response.data.ContentHtml) || "";
  } catch (error) {
    return "";
  }
}

function getPageElements(vm, pageSelector = DEFAULT_PAGE_SELECTOR) {
  const root = vm && vm.$refs ? vm.$refs.dataprint : null;
  if (!root || !root.querySelectorAll) {
    return [];
  }

  return Array.from(root.querySelectorAll(pageSelector));
}

function getMeasureElement(vm, measureRef = "guaranteeMeasure") {
  if (!vm || !vm.$refs) {
    return null;
  }

  const ref = vm.$refs[measureRef];
  return ref && ref.$el ? ref.$el : ref || null;
}

function getRenderedPageContentBottom(pageElement) {
  if (!pageElement || !pageElement.children) {
    return 0;
  }

  const pageTop = pageElement.getBoundingClientRect().top;

  return Array.from(pageElement.children)
    .filter((element) => {
      if (!element || !element.getBoundingClientRect) {
        return false;
      }

      const styles = window.getComputedStyle(element);
      if (styles.position === "absolute" || styles.position === "fixed") {
        return false;
      }

      const rect = element.getBoundingClientRect();
      return rect.width > 0 || rect.height > 0;
    })
    .reduce((maxBottom, element) => {
      return Math.max(
        maxBottom,
        element.getBoundingClientRect().bottom - pageTop
      );
    }, 0);
}

async function measureGuaranteeBlockHeight(vm, {
  businessId,
  branchId,
  guaranteeType,
  fontSize = "11px",
} = {}) {
  if (hasEmptyGuaranteeCache(vm, { businessId, branchId, guaranteeType })) {
    return 0;
  }

  const html = String(vm && vm.guaranteeHtml ? vm.guaranteeHtml : "").trim();
  if (!html) {
    return 0;
  }

  const ownerDocument = (vm && vm.$el && vm.$el.ownerDocument) || document;
  const host = ownerDocument.createElement("div");
  let instance = null;

  host.style.position = "fixed";
  host.style.left = "-20000px";
  host.style.top = "0";
  host.style.width = "1000px";
  host.style.visibility = "hidden";
  host.style.pointerEvents = "none";
  host.style.zIndex = "-1";
  ownerDocument.body.appendChild(host);

  try {
    const GuaranteeCtor = Vue.extend(GuaranteePrintBlock);
    instance = new GuaranteeCtor({
      propsData: {
        html,
        businessId,
        branchId,
        guaranteeType,
        fontSize,
      },
    });

    instance.$mount();
    host.appendChild(instance.$el);

    await vm.$nextTick();

    return instance.$el.getBoundingClientRect().height || 0;
  } catch (error) {
    return 0;
  } finally {
    if (instance && typeof instance.$destroy === "function") {
      instance.$destroy();
    }

    host.remove();
  }
}

export async function calculateGuaranteePlacement(vm, {
  pageSelector = DEFAULT_PAGE_SELECTOR,
  measureRef = "guaranteeMeasure",
  guaranteeType = "COMPROBANTE",
  fontSize = "11px",
  fitMarginPx = DEFAULT_FIT_MARGIN_PX,
} = {}) {
  const pages = getPageElements(vm, pageSelector);
  const measureElement = getMeasureElement(vm, measureRef);
  const guaranteeHtml = String(vm && vm.guaranteeHtml ? vm.guaranteeHtml : "").trim();

  if (!guaranteeHtml && hasEmptyGuaranteeCache(vm, {
    businessId: vm && vm.guaranteeBusinessId,
    branchId: vm && vm.guaranteeBranchId,
    guaranteeType,
  })) {
    return {
      ready: false,
      inline: false,
      guaranteeHeight: 0,
      availableHeight: 0,
    };
  }

  if (!pages.length || !guaranteeHtml) {
    return {
      ready: false,
      inline: false,
      guaranteeHeight: 0,
      availableHeight: 0,
    };
  }

  const lastPage = pages[pages.length - 1];
  const pageHeight = lastPage.clientHeight || lastPage.getBoundingClientRect().height || 0;
  const usedHeight = getRenderedPageContentBottom(lastPage);
  const guaranteeHeight = measureElement
    ? (measureElement.getBoundingClientRect().height || measureElement.scrollHeight || 0)
    : await measureGuaranteeBlockHeight(vm, {
        businessId: vm.guaranteeBusinessId,
        branchId: vm.guaranteeBranchId,
        guaranteeType,
        fontSize,
      });
  const availableHeight = Math.max(0, pageHeight - usedHeight - fitMarginPx);

  return {
    ready: guaranteeHeight > 0,
    inline: guaranteeHeight > 0 && guaranteeHeight <= availableHeight,
    guaranteeHeight,
    availableHeight,
  };
}

export async function resolveGuaranteePlacement(vm, options = {}) {
  if (!String(vm && vm.guaranteeHtml ? vm.guaranteeHtml : "").trim()) {
    vm.showGuaranteeInline = false;
    vm.guaranteePlacementResolved = true;
    return {
      ready: false,
      inline: false,
      guaranteeHeight: 0,
      availableHeight: 0,
    };
  }

  const token = (vm.guaranteePlacementToken || 0) + 1;
  vm.guaranteePlacementToken = token;
  vm.showGuaranteeInline = false;
  vm.guaranteePlacementResolved = false;

  for (let attempt = 0; attempt < 30; attempt += 1) {
    await vm.$nextTick();
    const placement = await calculateGuaranteePlacement(vm, options);

    if (placement.ready) {
      if (vm.guaranteePlacementToken !== token) {
        return placement;
      }

      vm.showGuaranteeInline = placement.inline;
      vm.guaranteePlacementResolved = true;
      return placement;
    }

    await sleep(80);
  }

  if (vm.guaranteePlacementToken === token) {
    vm.showGuaranteeInline = false;
    vm.guaranteePlacementResolved = true;
  }

  return {
    ready: false,
    inline: false,
    guaranteeHeight: 0,
    availableHeight: 0,
  };
}

export async function prepareGuaranteePlacement(vm, loadOptions, placementOptions) {
  if (!String(vm.guaranteeHtml || "").trim()) {
    vm.guaranteeHtml = await loadGuaranteeHtml(vm, loadOptions);
  }

  if (!String(vm.guaranteeHtml || "").trim()) {
    vm.showGuaranteeInline = false;
    vm.guaranteePlacementResolved = true;
    return;
  }

  await resolveGuaranteePlacement(vm, placementOptions);
}
